import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Check, Heart, Zap, Star } from "lucide-react";
import { toast } from "sonner";
import { formatAED } from "@/data/products";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";

export const RatingStars = ({ rating, count, size = "h-3 w-3" }) => (
  <span className="flex items-center gap-1" data-testid="rating-stars">
    <span className="flex">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${size} ${i <= Math.round(rating) ? "text-amber-400" : "text-line"}`}
          fill="currentColor"
        />
      ))}
    </span>
    <span className="text-[11px] font-bold text-brandink">{rating}</span>
    {count !== undefined && <span className="text-[10px] text-muted">({count})</span>}
  </span>
);

export default function ProductCard({ product }) {
  const { addItem, setCartOpen, categories, wishlist, toggleWishlist } = useCart();
  const { t, isAr } = useLanguage();
  const navigate = useNavigate();
  const [added, setAdded] = useState(false);
  const wished = wishlist.includes(product.id);

  const open = () => navigate(`/product/${product.id}`);

  const onAdd = () => {
    addItem(product.id);
    setAdded(true);
    toast.success(isAr ? `${product.name} — ${t("toast_added")}` : `${product.name} ${t("toast_added")}`, {
      action: { label: t("toast_view"), onClick: () => setCartOpen(true) },
    });
    setTimeout(() => setAdded(false), 1600);
  };

  const cat = categories.find((c) => c.id === product.category);
  const isBestSeller = product.badge === "Best Seller";

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className={`group relative flex h-full flex-col overflow-hidden rounded-lg border bg-white transition-shadow duration-200 hover:shadow-md ${
        isBestSeller ? "border-brand ring-1 ring-brand" : "border-line"
      }`}
    >
      {product.badge && (
        <span
          data-testid={`product-badge-${product.id}`}
          className={`absolute start-2 top-2 z-10 rounded px-1.5 py-0.5 text-[10px] font-bold ${
            isBestSeller
              ? "bg-brand text-brandink shadow-sm ring-1 ring-black/10"
              : product.badge === "New"
                ? "bg-success text-white"
                : "bg-brand text-brandink"
          }`}
        >
          {(isAr && product.badge_ar) || product.badge}
        </span>
      )}
      <button
        data-testid={`product-wishlist-${product.id}`}
        onClick={() => toggleWishlist(product.id)}
        className={`absolute end-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full border bg-white shadow-sm transition-colors ${
          wished ? "border-destructive text-destructive" : "border-line text-muted hover:text-destructive"
        }`}
        aria-label={t("nav_wishlist")}
      >
        <Heart className="h-4 w-4" fill={wished ? "currentColor" : "none"} />
      </button>
      <button
        data-testid={`product-view-${product.id}`}
        onClick={open}
        className="flex h-44 w-full cursor-pointer items-center justify-center bg-white p-4 sm:h-48"
        aria-label={`View ${product.name}`}
      >
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
        />
      </button>
      <div className="flex flex-1 flex-col px-3 pb-3">
        <p className="text-[10px] uppercase tracking-wide text-muted">
          {isAr ? cat?.name_ar : cat?.name}
        </p>
        <button
          data-testid={`product-name-${product.id}`}
          onClick={open}
          className="mt-0.5 min-h-10 cursor-pointer text-start text-sm font-medium leading-snug text-brandink line-clamp-2 transition-colors hover:text-link"
        >
          {product.name}
        </button>
        <div className="mt-1">
          <RatingStars rating={product.rating || 4.5} count={product.rating_count} />
        </div>
        <p className="mt-auto pt-2 text-lg font-extrabold text-brandink" data-testid={`product-price-${product.id}`}>
          {formatAED(product.price)}
        </p>
        {product.express && (
          <span
            data-testid={`product-express-${product.id}`}
            className="mt-1 inline-flex w-fit items-center gap-1 rounded bg-brand px-1.5 py-0.5 text-[10px] font-extrabold lowercase italic text-brandink"
          >
            <Zap className="h-3 w-3" fill="currentColor" />
            {t("express")}
          </span>
        )}
        <button
          data-testid={`product-add-${product.id}`}
          onClick={onAdd}
          className={`mt-2 flex w-full items-center justify-center gap-1.5 rounded-md py-2 text-xs font-bold transition-colors duration-200 ${
            added
              ? "bg-success text-white"
              : "bg-brand text-brandink hover:bg-branddark"
          }`}
        >
          {added ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {added ? t("added") : t("add_to_cart")}
        </button>
      </div>
    </article>
  );
}
